// ─── Where the portfolio's revenue comes from ────────────────────────────────
//
// One axis: geography. Not a matrix, and the reason is worth stating because it
// was measured rather than assumed.
//
// `company_segments` holds each company's revenue split by BUSINESS segment and
// by GEOGRAPHY as two separate breakdowns — marginals, never the joint cell.
// The filings do carry facts tagged with both axes at once (72 of them across
// five sampled companies), but sprint 2 deliberately skipped every one of them
// as "two segment axes at once". So "iPhone revenue in Europe" is not in the
// table, and a sector × region matrix could only be produced by multiplying the
// two marginals — which assumes the two are independent. They are not: AWS has
// nothing like the geographic profile of Amazon's retail arm. That product
// would be a number no company ever reported.
//
// The business axis could not carry a matrix anyway: 123 distinct segment
// labels across the universe, only 7 shared by more than one company, and five
// of those seven are region names that leaked into the business axis. It is not
// a taxonomy — it is each company's own internal reporting split.
//
// Geography is different: 54 labels, all of them naming real places, and
// normalisable by an explicit map (src/constants/regions.js).

import { resolveCompanyRegions, classifyRegionLabel, REGIONS } from "../constants/regions";

/**
 * The latest fiscal year a company reported geography for.
 * Rows from older years are ignored rather than blended — mixing two years
 * into one share would describe a period that never existed.
 */
function latestRows(segments, type) {
  const rows = segments.filter(s => s.segment_type === type && s.revenue != null);
  if (!rows.length) return { rows: [], year: null };
  const year = Math.max(...rows.map(s => s.fiscal_year));
  return { rows: rows.filter(s => s.fiscal_year === year), year };
}

// The company's own geographic split, from whichever axis it filed it on.
//
// Apple's reportable segments ARE regions · Americas, Europe, Greater China,
// Japan, Rest of Asia Pacific · but it tags them on the BUSINESS axis. The
// geographic axis carries a coarser country note (US · China · "Other
// countries" 48%). Reading only the geographic axis showed half of Apple as
// "not broken out" when Apple breaks it out in full (found 2026-09-25).
//
// So the business axis is a candidate too, but only when EVERY label on it is
// a place the region map knows · Amazon's "North America · International ·
// AWS" is not, and stays a business split. Of the candidates, the one that
// leaves less revenue unplaced wins; on a tie, the geographic axis.
function unplacedShare(rows) {
  const r = resolveCompanyRegions(rows);
  if (!r.base) return 1;
  return ((r.byRegion.intl_unspecified || 0) + (r.byRegion.rest_unspecified || 0) + (r.byRegion.other || 0)) / r.base;
}

export function regionRows(segments) {
  const geo = latestRows(segments, "geographic");
  const biz = latestRows(segments, "business");
  const bizIsPlaces = biz.rows.length > 1 && biz.rows.every(r => {
    const c = classifyRegionLabel(r.segment_name);
    return c.kind === "leaf" && c.region !== "other";
  });
  // Never trade a newer year for an older one: real numbers from the wrong
  // period are worse than a coarser split of the right one.
  if (!bizIsPlaces || (geo.year != null && biz.year < geo.year)) return { ...geo, axis: "geographic" };
  if (!geo.rows.length) return { ...biz, axis: "business" };
  return unplacedShare(biz.rows) < unplacedShare(geo.rows)
    ? { ...biz, axis: "business" }
    : { ...geo, axis: "geographic" };
}

/**
 * Portfolio exposure by region.
 *
 * @param holdings [{ symbol, currentPrice, shares }]
 * @param segmentsBySymbol { SYM: [segment rows] }
 * @returns {
 *   regions:  [{ id, pct, weight }]        share OF THE COVERED portfolio
 *   covered:  { count, total, weightPct }  how much of the portfolio is in
 *   excluded: [{ symbol, weightPct, reason }]
 *   emptyRegions: [id]                     regions with no exposure at all
 *   notes:    { droppedSubtotals, unknownLabels }
 * }
 */
export function computeExposure(holdings = [], segmentsBySymbol = {}) {
  const valued = holdings
    .map(h => ({ ...h, value: (Number(h.currentPrice) || 0) * (Number(h.shares) || 0) }))
    .filter(h => h.value > 0);

  const portfolioValue = valued.reduce((a, h) => a + h.value, 0);
  if (!portfolioValue) {
    return {
      regions: [], covered: { count: 0, total: valued.length, weightPct: 0 },
      excluded: [], emptyRegions: REGIONS.map(r => r.id),
      notes: { droppedSubtotals: [], unknownLabels: [] }, portfolioValue: 0,
    };
  }

  const byRegion = {};
  const excluded = [];
  const droppedSubtotals = [];
  const unknownLabels = new Set();
  const perHolding = [];
  let coveredValue = 0;
  let coveredCount = 0;

  for (const h of valued) {
    const weight = h.value / portfolioValue;
    const segments = segmentsBySymbol[h.symbol];

    if (!segments || !segments.length) {
      excluded.push({ symbol: h.symbol, weightPct: weight * 100, reason: "no_data" });
      continue;
    }

    const { rows, year, axis } = regionRows(segments);
    if (!rows.length) {
      // The company IS in the data set — it simply does not break revenue down
      // by geography. That is a different fact from "we have nothing on it",
      // and the screen states which of the two applies.
      excluded.push({ symbol: h.symbol, weightPct: weight * 100, reason: "no_geographic_breakdown" });
      continue;
    }

    const resolved = resolveCompanyRegions(rows);
    if (!resolved.base) {
      excluded.push({ symbol: h.symbol, weightPct: weight * 100, reason: "no_geographic_breakdown" });
      continue;
    }

    for (const d of resolved.dropped) droppedSubtotals.push({ symbol: h.symbol, ...d });
    for (const u of resolved.unknown) unknownLabels.add(u);

    // The holding's weight is split across regions in the proportions the
    // company itself reported. `base` is the revenue the company actually
    // broke down — not its total revenue, which is not in this table — so the
    // shares are "of the revenue it split", and the screen says so.
    for (const [region, revenue] of Object.entries(resolved.byRegion)) {
      byRegion[region] = (byRegion[region] || 0) + weight * (revenue / resolved.base);
    }
    // Which year this holding's split comes from, and how much of it the
    // company did not place anywhere · the exposure screen names both.
    const unsplit = (resolved.byRegion.intl_unspecified || 0) + (resolved.byRegion.rest_unspecified || 0);
    const labelShares = Object.fromEntries(rows.filter(r => r.revenue > 0).map(r => [r.segment_name, resolved.base > 0 ? r.revenue / resolved.base : 0]));
    perHolding.push({ symbol: h.symbol, year, axis, labels: rows.map(r => r.segment_name), labelShares, unspecifiedShare: resolved.base > 0 ? unsplit / resolved.base : 0 });
    coveredValue += h.value;
    coveredCount += 1;
  }

  const totalShare = Object.values(byRegion).reduce((a, b) => a + b, 0);

  const regions = REGIONS.map(r => ({
    id: r.id,
    // Whether this bucket is a place. Three of them are not: they describe what
    // a filing disclosed, and "no exposure to 'not broken out'" is not a
    // sentence about the world.
    place: r.place !== false,
    // Share of the WHOLE portfolio, so the numbers stay comparable to the
    // coverage figure and never imply they add to 100%.
    pct: (byRegion[r.id] || 0) * 100,
    hasData: byRegion[r.id] != null,
  })).sort((a, b) => b.pct - a.pct);

  return {
    regions,
    covered: {
      count: coveredCount,
      total: valued.length,
      weightPct: portfolioValue ? (coveredValue / portfolioValue) * 100 : 0,
    },
    excluded: excluded.sort((a, b) => b.weightPct - a.weightPct),
    emptyRegions: regions.filter(r => !r.hasData && r.place).map(r => r.id),
    notes: { droppedSubtotals, unknownLabels: [...unknownLabels] },
    perHolding,
    totalSharePct: totalShare * 100,
    portfolioValue,
  };
}
